import React, { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';

interface ChangePasswordModalProps {
  open: boolean;
  patientName?: string;
  onSave: (password: string) => Promise<void>;
  onCancel: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  open,
  patientName,
  onSave,
  onCancel,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open) {
      setPassword('');
      setConfirmPassword('');
      setError('');
      setSubmitError('');
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSave = async () => {
    setSubmitError('');

    if (!password.trim()) {
      setError('La contraseña es requerida');
      return;
    }
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      await onSave(password);
    } catch (err: any) {
      console.error('Error updating password:', err);
      // Surface field-specific 400 errors (e.g. { password: ["too common", ...] })
      const resp = err?.response;
      if (resp && Array.isArray(resp.password) && resp.password.length > 0) {
        setSubmitError(resp.password.join(' '));
      } else if (resp && typeof resp === 'object') {
        const messages = Object.values(resp)
          .flat()
          .filter((m): m is string => typeof m === 'string');
        setSubmitError(messages.length > 0 ? messages.join(' ') : (err?.message || 'Error al cambiar la contraseña. Por favor intente nuevamente.'));
      } else {
        setSubmitError(err?.message || 'Error al cambiar la contraseña. Por favor intente nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '12px' : '20px',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: isMobile ? '24px 20px' : '32px 40px',
        width: '100%',
        maxWidth: 480,
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: 22, fontWeight: 700, color: '#1f2937' }}>
          Cambiar Contraseña
        </h2>

        {/* Risk warning */}
        <div style={{
          background: '#fffbeb',
          border: '1px solid #fbbf24',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
        }}>
          <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>⚠️</span>
          <div style={{ fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
            <strong>Acción de riesgo</strong>
            <div style={{ marginTop: 4 }}>
              Vas a restablecer la contraseña de{' '}
              <strong>{patientName || 'este paciente'}</strong>. La contraseña anterior dejará de funcionar
              y el paciente podría perder el acceso al servicio si no le comunicas la nueva contraseña.
              Asegúrate de informarle del cambio.
            </div>
          </div>
        </div>

        {submitError && (
          <div style={{
            background: '#fee2e2',
            color: '#dc2626',
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 20,
            fontSize: 14,
          }}>
            {submitError}
          </div>
        )}

        {/* New password */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Nueva Contraseña <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input
            type="password"
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            autoComplete="new-password"
            style={{
              width: '100%',
              padding: '11px 14px',
              border: `1px solid ${error ? '#dc2626' : '#cbd5e1'}`,
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s',
            }}
            placeholder="Mínimo 8 caracteres"
          />
        </label>

        {/* Confirm password */}
        <label style={{ display: 'block', marginBottom: 24 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Confirmar Contraseña <span style={{ color: '#dc2626' }}>*</span>
          </span>
          <input
            type="password"
            value={confirmPassword}
            onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
            autoComplete="new-password"
            style={{
              width: '100%',
              padding: '11px 14px',
              border: `1px solid ${error ? '#dc2626' : '#cbd5e1'}`,
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.2s',
            }}
            placeholder="Repite la nueva contraseña"
          />
          {error && (
            <span style={{ color: '#dc2626', fontSize: 13, marginTop: 6, display: 'block' }}>
              {error}
            </span>
          )}
        </label>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              background: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: 6,
              padding: '11px 24px',
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: 15,
              opacity: isSubmitting ? 0.6 : 1,
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = '#e5e7eb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            style={{
              background: isSubmitting ? '#fcd34d' : '#f59e0b',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '11px 24px',
              fontWeight: 600,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: 15,
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = '#d97706'; }}
            onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.background = '#f59e0b'; }}
          >
            {isSubmitting ? 'Guardando...' : 'Cambiar Contraseña'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
